import { useState, useEffect, useRef, ReactNode } from 'react';
import clsx from 'clsx';

type EditableProps = {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  placeholder?: string;
  multiline?: boolean;
  type?: 'text' | 'number' | 'date' | 'email';
  className?: string;
  inputClassName?: string;
  display?: (v: string) => ReactNode;
  validate?: (v: string) => string | null;
  empty?: string;
};

export function Editable({
  value, onSave, placeholder, multiline, type = 'text',
  className, inputClassName, display, validate, empty = 'click to add',
}: EditableProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 10); }, [editing]);

  async function commit() {
    if (draft === value) { setEditing(false); return; }
    if (validate) {
      const e = validate(draft);
      if (e) { setErr(e); return; }
    }
    setBusy(true); setErr(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e: any) {
      setErr(e.message ?? 'save failed');
    } finally {
      setBusy(false);
    }
  }
  function cancel() {
    setDraft(value); setEditing(false); setErr(null);
  }

  if (editing) {
    const Tag: any = multiline ? 'textarea' : 'input';
    return (
      <span className={clsx('inline-flex flex-col gap-0.5', className)}>
        <Tag
          ref={inputRef as any}
          type={multiline ? undefined : type}
          value={draft}
          onChange={(e: any) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter' && !e.shiftKey && !multiline) { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          placeholder={placeholder}
          disabled={busy}
          rows={multiline ? 3 : undefined}
          className={clsx(
            'bg-transparent hairline rounded-sm px-1 py-0.5 text-inherit font-inherit outline-none focus:border-ink',
            inputClassName,
          )}
        />
        {err && <span className="text-xs text-negative">{err}</span>}
      </span>
    );
  }

  const isEmpty = !value || value === '';
  return (
    <span
      onClick={() => setEditing(true)}
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setEditing(true))}
      className={clsx(
        'inline-block cursor-text rounded-sm px-1 -mx-1 hover:bg-whisper transition-colors duration-[120ms]',
        'border border-transparent hover:border-hairline',
        className,
      )}
      title="click to edit"
    >
      {isEmpty ? <span className="text-muted italic">{empty}</span> : (display ? display(value) : value)}
    </span>
  );
}

// Editable enum/select
export function EditableSelect({
  value, options, onSave, className,
}: {
  value: string;
  options: { value: string; label: string }[];
  onSave: (next: string) => Promise<void> | void;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <select
      value={value}
      disabled={busy}
      onChange={async e => {
        const next = e.target.value;
        if (next === value) return;
        setBusy(true);
        try { await onSave(next); }
        finally { setBusy(false); }
      }}
      className={clsx('bg-transparent hairline rounded-sm px-1 py-0.5 text-inherit hover:border-ink cursor-pointer', className)}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
